import React, { useState, useEffect } from 'react';
import assumptionsData from '../data/assumptions_full.json';

const AssumptionsModal = ({ isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState('Info');
  const [challenges, setChallenges] = useState({});
  const [showChallengeDialog, setShowChallengeDialog] = useState(false);
  const [currentChallenge, setCurrentChallenge] = useState({ sheet: '', row: '', param: '' });
  const [challengeForm, setChallengeForm] = useState({ comment: '', reference: '' });
  const [submissionStatus, setSubmissionStatus] = useState(null); // 'success', 'error', or null

  // Load challenges from localStorage
  useEffect(() => {
    const savedChallenges = localStorage.getItem('assumptionsChallenges');
    if (savedChallenges) {
      setChallenges(JSON.parse(savedChallenges));
    }
  }, []);

  // Save challenges to localStorage
  const saveChallenges = (newChallenges) => {
    setChallenges(newChallenges);
    localStorage.setItem('assumptionsChallenges', JSON.stringify(newChallenges));
  };

  const openChallengeDialog = (sheet, rowIdx, param) => {
    const key = `${sheet}-${rowIdx}`;
    setCurrentChallenge({ sheet, row: rowIdx, param });
    if (challenges[key]) {
      setChallengeForm(challenges[key]);
    } else {
      setChallengeForm({ comment: '', reference: '' });
    }
    setShowChallengeDialog(true);
  };

  const submitChallenge = async () => {
    const key = `${currentChallenge.sheet}-${currentChallenge.row}`;
    const newChallenges = { ...challenges };

    if (challengeForm.comment || challengeForm.reference) {
      newChallenges[key] = { ...challengeForm };

      // Submit to Netlify Forms
      try {
        const formData = new FormData();
        formData.append('form-name', 'assumption-challenges');
        formData.append('parameter', currentChallenge.param);
        formData.append('sheet', currentChallenge.sheet);
        formData.append('row', currentChallenge.row);
        formData.append('comment', challengeForm.comment);
        formData.append('reference', challengeForm.reference);
        formData.append('timestamp', new Date().toISOString());

        await fetch('/', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams(formData).toString()
        });

        setSubmissionStatus('success');
        setTimeout(() => setSubmissionStatus(null), 5000); // Hide after 5 seconds
      } catch (error) {
        console.error('Error submitting challenge:', error);
        setSubmissionStatus('error');
        setTimeout(() => setSubmissionStatus(null), 5000); // Hide after 5 seconds
      }
    } else {
      delete newChallenges[key];
    }

    saveChallenges(newChallenges);
    setShowChallengeDialog(false);
    setChallengeForm({ comment: '', reference: '' });
  };

  const handleTabClick = (tabKey) => {
    // Only allow clicking on the Info tab, other tabs are locked
    if (tabKey === 'Info') {
      setActiveTab(tabKey);
    }
  };

  if (!isOpen) return null;

  const tabs = [
    { key: 'Info', label: 'Key Specs', icon: '📋' },
    { key: 'Polysilicon', label: 'Polysilicon', icon: '🔬' },
    { key: 'Ingot Wafer', label: 'Ingot/Wafer', icon: '💎' },
    { key: 'Cell', label: 'Cell', icon: '⚡' },
    { key: 'Module', label: 'Module', icon: '📦' },
    { key: 'Factory Details', label: 'Factory', icon: '🏭' },
    { key: 'Transportation', label: 'Transport', icon: '🚚' },
    { key: 'Location Factors', label: 'Location', icon: '📍' },
    { key: 'Policy', label: 'Policy', icon: '📜' }
  ];

  const renderTable = (sheetName) => {
    const data = assumptionsData[sheetName];
    if (!data || data.length === 0) return <p>No data available</p>;

    const isLocked = sheetName !== 'Info';

    // First row is header
    const headers = data[0].filter(h => h !== '');
    const rows = data.slice(1);

    return (
      <div className="overflow-x-auto relative">
        {isLocked && (
          <div className="absolute inset-0 backdrop-blur-md bg-white/30 z-10 flex items-center justify-center">
            <div className="text-center p-6 max-w-md">
              <svg className="w-16 h-16 mx-auto mb-4" style={{ color: '#E78E42' }} fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <p className="text-lg font-bold mb-2" style={{ color: '#000755' }}>Confidential & Proprietary Data</p>
              <p className="text-sm text-gray-600 mb-2">
                These detailed parameter values are confidential and proprietary.
              </p>
              <p className="text-xs text-gray-500">
                For access or bilateral discussion about specific parameters, please contact us directly.
              </p>
            </div>
          </div>
        )}
        <div className={isLocked ? 'blur-sm pointer-events-none select-none' : ''}>
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr style={{ backgroundImage: 'linear-gradient(to right, rgba(231, 142, 66, 0.2), rgba(234, 193, 68, 0.2))' }}>
              {headers.map((header, idx) => (
                <th
                  key={idx}
                  className="px-3 py-2 text-left font-bold border-b-2 text-xs"
                  style={{ borderColor: '#E78E42', color: '#000755' }}
                >
                  {header}
                </th>
              ))}
              <th
                className="px-3 py-2 text-left font-bold border-b-2 text-xs"
                style={{ borderColor: '#E78E42', color: '#000755' }}
              >
                Challenge
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => {
              // Filter out empty rows
              const nonEmptyCells = row.filter(cell => cell !== '');
              if (nonEmptyCells.length === 0) return null;

              const challengeKey = `${sheetName}-${rowIdx}`;
              const hasChallenge = challenges[challengeKey];

              return (
                <tr
                  key={rowIdx}
                  className={`border-b ${rowIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-orange-50 transition-colors`}
                >
                  {row.slice(0, headers.length).map((cell, cellIdx) => (
                    <td
                      key={cellIdx}
                      className="px-3 py-2 text-gray-700 text-xs"
                      style={cellIdx === 0 ? { fontWeight: '600', color: '#0A3C80' } : {}}
                    >
                      {/* Show parameter names (first column) and values only for Info tab */}
                      {cellIdx === 0 ? cell : (sheetName === 'Info' ? cell : '')}
                    </td>
                  ))}
                  <td className="px-3 py-2 text-xs">
                    <button
                      onClick={() => openChallengeDialog(sheetName, rowIdx, row[0])}
                      className={`px-3 py-1 rounded text-xs font-semibold transition-colors ${
                        hasChallenge
                          ? 'bg-orange-500 text-white hover:bg-orange-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                      title={hasChallenge ? 'View/Edit Challenge' : 'Add Challenge'}
                    >
                      {hasChallenge ? '💬' : '+'}
                    </button>
                    {hasChallenge && (
                      <div className="mt-1 p-2 bg-orange-50 rounded text-xs border border-orange-200">
                        <div className="font-semibold text-orange-800">Comment:</div>
                        <div className="text-gray-700 mb-1">{hasChallenge.comment}</div>
                        {hasChallenge.reference && (
                          <>
                            <div className="font-semibold text-orange-800">Reference:</div>
                            <div className="text-blue-600 break-all">{hasChallenge.reference}</div>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
      {/* Success/Error Notification */}
      {submissionStatus && (
        <div className="fixed top-4 right-4 z-[10001] animate-slide-in">
          <div className={`rounded-lg shadow-2xl p-4 flex items-start gap-3 max-w-md ${
            submissionStatus === 'success'
              ? 'bg-green-50 border-2 border-green-500'
              : 'bg-red-50 border-2 border-red-500'
          }`}>
            {submissionStatus === 'success' ? (
              <svg className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            ) : (
              <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            )}
            <div className="flex-1">
              <h4 className={`font-bold mb-1 ${submissionStatus === 'success' ? 'text-green-900' : 'text-red-900'}`}>
                {submissionStatus === 'success' ? 'Challenge Submitted!' : 'Submission Failed'}
              </h4>
              <p className={`text-sm ${submissionStatus === 'success' ? 'text-green-800' : 'text-red-800'}`}>
                {submissionStatus === 'success'
                  ? 'Thank you! Your challenge has been submitted successfully and will be reviewed by our team.'
                  : 'Your challenge was saved locally but could not be submitted online. Please check your connection and try again.'}
              </p>
            </div>
            <button
              onClick={() => setSubmissionStatus(null)}
              className={`flex-shrink-0 ${submissionStatus === 'success' ? 'text-green-600 hover:text-green-800' : 'text-red-600 hover:text-red-800'}`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b-4" style={{ borderColor: '#E78E42', backgroundImage: 'linear-gradient(to right, rgba(231, 142, 66, 0.1), rgba(234, 193, 68, 0.1))' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-8 h-8" style={{ color: '#E78E42' }} fill="currentColor" viewBox="0 0 20 20">
                <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z" />
                <path fillRule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clipRule="evenodd" />
              </svg>
              <div>
                <h2 className="text-3xl font-bold text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(to right, #0A3C80, #E78E42)' }}>
                  Model Assumptions & Parameters
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Comprehensive technical and financial assumptions from PV Made in Europe – are Proposed Policy Measures Sufficient?, EUPVSEC 2025
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors p-2 rounded-lg hover:bg-gray-100"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Confidentiality Disclaimer */}
        <div className="px-6 py-3 bg-gradient-to-r from-yellow-50 to-orange-50 border-b-2 border-yellow-400">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-700 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-yellow-900">Confidentiality Notice</p>
              <p className="text-xs text-yellow-800 mt-1">
                Detailed parameter values from Polysilicon to Policy tabs are confidential and proprietary.
                For access to these datasets or to discuss specific parameters, please contact us for bilateral discussion.
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b-2" style={{ borderColor: '#E78E42' }}>
          <div className="flex overflow-x-auto">
            {tabs.map(tab => {
              const isTabLocked = tab.key !== 'Info';
              return (
                <button
                  key={tab.key}
                  onClick={() => handleTabClick(tab.key)}
                  className={`px-4 py-3 font-semibold text-sm whitespace-nowrap transition-all border-b-4 relative ${
                    activeTab === tab.key
                      ? 'text-white'
                      : isTabLocked
                        ? 'border-transparent cursor-not-allowed opacity-60'
                        : 'border-transparent hover:bg-gray-50'
                  }`}
                  style={
                    activeTab === tab.key
                      ? {
                          backgroundImage: 'linear-gradient(to right, #E78E42, #EAC144)',
                          borderColor: '#000755'
                        }
                      : { color: '#0A3C80' }
                  }
                  disabled={isTabLocked}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.label}
                  {isTabLocked && (
                    <span className="ml-2 text-xs">🔒</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="mb-4 p-4 rounded-lg border-2" style={{ backgroundImage: 'linear-gradient(to right, rgba(10, 60, 128, 0.05), rgba(231, 142, 66, 0.05))', borderColor: '#0A3C80' }}>
            <h3 className="font-bold mb-2" style={{ color: '#000755' }}>
              {tabs.find(t => t.key === activeTab)?.label} {activeTab !== 'Info' && 'Stage Parameters'}
            </h3>
            <p className="text-sm text-gray-700">
              {activeTab === 'Info' && 'Key module and technology specifications: power, efficiency, dimensions, material consumption, and financial parameters'}
              {activeTab === 'Polysilicon' && 'Material costs, usage rates, and manufacturing parameters for polysilicon production'}
              {activeTab === 'Ingot Wafer' && 'Ingot and wafer manufacturing costs, equipment, and material specifications'}
              {activeTab === 'Cell' && 'Solar cell manufacturing parameters, equipment costs, and material usage'}
              {activeTab === 'Module' && 'Module assembly costs, materials (glass, frame, backsheet, etc.), and manufacturing parameters'}
              {activeTab === 'Factory Details' && 'Factory capacity, CAPEX, OPEX, labor, energy, and utilization parameters'}
              {activeTab === 'Transportation' && 'Transportation costs and logistics parameters between manufacturing stages'}
              {activeTab === 'Location Factors' && 'Location-specific factors: labor costs, energy prices, and regional adjustments'}
              {activeTab === 'Policy' && 'Policy instruments, subsidies, tariffs, and regulatory parameters'}
            </p>
          </div>

          {renderTable(activeTab)}
        </div>

        {/* Footer */}
        <div className="p-4 border-t-2 flex items-center justify-between" style={{ borderColor: '#E78E42', backgroundColor: 'rgba(234, 193, 68, 0.05)' }}>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <svg className="w-4 h-4" style={{ color: '#0A3C80' }} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span>Source: <strong>PV Made in Europe – are Proposed Policy Measures Sufficient?, EUPVSEC 2025</strong></span>
          </div>
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-shadow"
            style={{ backgroundImage: 'linear-gradient(to right, #0A3C80, #000755)' }}
          >
            Close
          </button>
        </div>
      </div>

      {/* Challenge Dialog */}
      {showChallengeDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4" style={{ zIndex: 10000 }}>
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold" style={{ color: '#0A3C80' }}>
                Challenge Assumption: {currentChallenge.param}
              </h3>
              <button
                onClick={() => setShowChallengeDialog(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#000755' }}>
                  Comment / Challenge *
                </label>
                <textarea
                  value={challengeForm.comment}
                  onChange={(e) => setChallengeForm({ ...challengeForm, comment: e.target.value })}
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-orange-500"
                  rows="4"
                  placeholder="Provide your comment or challenge to this assumption..."
                  style={{ borderColor: '#E78E42' }}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold mb-2" style={{ color: '#000755' }}>
                  Reference (URL, Paper, etc.)
                </label>
                <input
                  type="text"
                  value={challengeForm.reference}
                  onChange={(e) => setChallengeForm({ ...challengeForm, reference: e.target.value })}
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-orange-500"
                  placeholder="https://example.com or paper citation"
                  style={{ borderColor: '#E78E42' }}
                />
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowChallengeDialog(false)}
                  className="px-4 py-2 rounded-lg font-semibold text-gray-700 bg-gray-200 hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitChallenge}
                  className="px-4 py-2 rounded-lg font-semibold text-white transition-colors"
                  style={{ backgroundImage: 'linear-gradient(to right, #E78E42, #EAC144)' }}
                >
                  {challenges[`${currentChallenge.sheet}-${currentChallenge.row}`] ? 'Update Challenge' : 'Submit Challenge'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssumptionsModal;
